import { useQuery } from '@tanstack/react-query';
import Global from '../../../../../Services/Common/Global';
import { apiRequest } from '../../../../../Services/Common/ApiRequest';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../../util';
import useSearchMutaion from '../../../../../hooks/apis/useSearchMutaion';
import { SafetyInfo_Schema } from './SafetyInfo_Schema';
import useSaveMutation from '../../../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../../../hooks/apis/useDeleteMutaion';

/**
 * Step01_SafetyInfo(안전보건정보) 조회 — RisCustom.GetRisCustom01R1
 * SaveRisBasicResult, Data = GetRisCustom01R1InfoItem[] (공정·설비·물질 포함)
 */
export const useSearch1 = (options = {}) => {
  const { rsCd, searchText = '', enabled = true, ...queryOptions } = options;
  return useQuery({
    queryKey: [
      'RiskEvaluation',
      'Step01_SafetyInfo',
      'getRisCustom01R1',
      rsCd,
      searchText,
    ],
    enabled: enabled !== false && Number(rsCd) > 0,
    queryFn: async () => {
      const params = mapParamsBySchema(SafetyInfo_Schema.mapper.search1, {
        rsCd,
        searchText,
      });
      const raw = await apiRequest(
        'post',
        Global.urls.RiskEvaluation.getRisCustom01R1,
        params
      );
      return raw;
    },
    ...queryOptions,
  });
};

/**
 * 공정 선택 모달 — 부서+공정 통합 조회 (GetRisCustom01R2)
 */
export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(SafetyInfo_Schema.mapper.search2, context);
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisCustom01R2,
      params
    );
  }, successOptions);
};

/**
 * 설비·제품 통합 조회 (getRisBasic01R4) — ItemSelectModal 설비 데이터
 */
export const useSearch3 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(SafetyInfo_Schema.mapper.search3, context);
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisCustom01R3,
      params
    );
  }, successOptions);
};

/**
 * Step01_SafetyInfo(안전보건정보) 저장
 * SaveRisCustom01U2 (공정/설비/물질)
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.save1);
    const req = payload?.[0] ?? {};
    if (!Number(req.RsCd)) {
      return {
        IsSuccess: false,
        Message: '유효한 RS_CD가 필요합니다.',
      };
    }
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisCustom01U2,
      req
    );
  }, options);
};

/**
 * 공정 선택 모달 공정등록 저장 (SaveRisCustom01U1)
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.save2);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisCustom01U1,
      payload
    );
  }, options);
};

/** Step01 공정명 변경 — UpdateRisCustomGongJongNm */
export const useSave5 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.save5);
    const req = payload?.[0] ?? {};
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.updateRisCustomGongJongNm,
      req
    );
  }, options);
};

/**
 * 설비 수기 저장 — SaveRisCustom01U3 (TB_BO_HAZ_BASIC_04, 신규 INSERT / 기존 UPDATE)
 * 본문: SaveRisCustom01U3Info[] → BuildBatchResponse
 */
export const useSave3 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.save3);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisCustom01U3,
      payload
    );
  }, options);
};

/**
 * 수기 설비 삭제 — DeleteRisCustom01D3 (DeleteRisCustom01D3Info[], CHAEBUN만 사용)
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.delete1);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.deleteRisCustom01D3,
      payload
    );
  }, options);
};

/**
 * 물질 수기 저장 — SaveRisCustom01U4 (TB_BO_HAM_BASIC_04, 신규 INSERT / 기존 UPDATE)
 */
export const useSave4 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.save4);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisCustom01U4,
      payload
    );
  }, options);
};

/**
 * 수기 물질 삭제 — DeleteRisCustom01D4 (DeleteRisCustom01D4Info[], HBD_ID만 사용)
 */
export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, SafetyInfo_Schema.mapper.delete2);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.deleteRisCustom01D4,
      payload
    );
  }, options);
};
